import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Image,
} from '@nextui-org/react';
import {
  MessageSquare,
  ArrowLeft,
  Pin,
  Lock,
  MessageCircle,
  Eye,
  Users,
  Edit3,
  Trash2,
  AlertTriangle,
  CornerDownRight,
  Bell,
  BellRing,
  Flag,
  Reply,
  CheckCircle2,
  X,
} from 'lucide-react';
import forumService, {
  ForumTopic,
  ForumPost,
  extractForumError,
} from '../services/forumService';
import { useAuth } from '../contexts/AuthContext';
import VoteButtons from '../components/forum/VoteButtons';
import ReplyComposer from '../components/forum/ReplyComposer';
import ReportModal from '../components/forum/ReportModal';
import EditTopicModal from '../components/forum/EditTopicModal';
import EditPostModal from '../components/forum/EditPostModal';
import ConfirmDeleteModal from '../components/forum/ConfirmDeleteModal';
import LoginPromptModal from '../components/forum/LoginPromptModal';

const TEAL = '#3FCED2';

type SortMode = 'best' | 'newest' | 'top';

interface Feedback {
  type: 'success' | 'error';
  text: string;
}

// ============= Vote-Helper =============

/** Wendet einen neuen Vote optimistisch auf die Zähler an. */
function applyVoteOptimistic<
  T extends {
    upvotes?: number;
    downvotes?: number;
    score?: number;
    userVote?: number;
  },
>(item: T, newVote: number): T {
  const prevVote = item.userVote ?? 0;
  let up = item.upvotes ?? 0;
  let down = item.downvotes ?? 0;
  if (prevVote === 1) up -= 1;
  if (prevVote === -1) down -= 1;
  if (newVote === 1) up += 1;
  if (newVote === -1) down += 1;
  up = Math.max(0, up);
  down = Math.max(0, down);
  return { ...item, upvotes: up, downvotes: down, score: up - down, userVote: newVote };
}

/** Aktualisiert einen Post im verschachtelten Antwort-Baum. */
function updatePostInTree(
  posts: ForumPost[],
  postId: string,
  updater: (post: ForumPost) => ForumPost,
): ForumPost[] {
  return posts.map((p) => {
    const id = p.id || p._id;
    if (id === postId) return updater(p);
    if (p.replies && p.replies.length > 0) {
      return { ...p, replies: updatePostInTree(p.replies, postId, updater) };
    }
    return p;
  });
}

const ForumTopicPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated } = useAuth();
  const currentUserId = (user?.id as string | undefined) || null;

  const [topic, setTopic] = useState<ForumTopic | null>(null);
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortMode>('best');

  // Interaktions-States
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loginPrompt, setLoginPrompt] = useState<string | null>(null);
  const [reportTarget, setReportTarget] = useState<{
    targetType: 'topic' | 'post';
    targetId: string;
  } | null>(null);
  const [editTopicOpen, setEditTopicOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<ForumPost | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'topic' | 'post';
    id: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState(false);

  const topicId = (topic?.id || topic?._id || null) as string | null;
  const isTopicAuthor =
    !!currentUserId && !!topic?.authorId && topic.authorId === currentUserId;
  const isLocked = topic?.isLocked === true || topic?.commentsDisabled === true;

  const showFeedback = useCallback((type: Feedback['type'], text: string) => {
    setFeedback({ type, text });
    window.setTimeout(() => setFeedback(null), 5000);
  }, []);

  const fetchTopic = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      setError(null);
      const t = await forumService.getTopicBySlug(slug);
      if (!t) {
        setError('Topic nicht gefunden');
        return;
      }
      setTopic(t);
    } catch (err) {
      console.error('Fehler beim Laden des Topics:', err);
      setError('Topic konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchPosts = useCallback(
    async (id: string) => {
      try {
        setLoadingPosts(true);
        const list = await forumService.getPostsByTopic(id, sortBy);
        setPosts(list);
      } catch (err) {
        console.error('Fehler beim Laden der Posts:', err);
      } finally {
        setLoadingPosts(false);
      }
    },
    [sortBy],
  );

  useEffect(() => {
    fetchTopic();
  }, [fetchTopic]);

  useEffect(() => {
    if (topicId) fetchPosts(topicId);
  }, [topicId, fetchPosts]);

  // ---------- Voting ----------
  const handleTopicVote = useCallback(
    async (newVote: 1 | -1 | 0) => {
      if (!isAuthenticated) {
        setLoginPrompt('Bitte melde dich an, um abzustimmen.');
        return;
      }
      if (!topic || !topicId) return;
      const prevVote = topic.userVote ?? 0;
      // Backend-Toggle-Semantik: gleicher voteType entfernt den Vote
      const sendType = newVote === 0 ? prevVote : newVote;
      if (sendType !== 1 && sendType !== -1) return;

      const snapshot = topic;
      setTopic((t) => (t ? applyVoteOptimistic(t, newVote) : t));
      try {
        const res = await forumService.vote('topic', topicId, sendType);
        setTopic((t) =>
          t
            ? {
                ...t,
                upvotes: res.upvotes ?? t.upvotes,
                downvotes: res.downvotes ?? t.downvotes,
                score: res.score ?? t.score,
                userVote: newVote,
              }
            : t,
        );
      } catch (err) {
        setTopic(snapshot);
        showFeedback('error', extractForumError(err, 'Vote fehlgeschlagen'));
      }
    },
    [isAuthenticated, topic, topicId, showFeedback],
  );

  const handlePostVote = useCallback(
    async (post: ForumPost, newVote: 1 | -1 | 0) => {
      if (!isAuthenticated) {
        setLoginPrompt('Bitte melde dich an, um abzustimmen.');
        return;
      }
      const pid = (post.id || post._id) as string | undefined;
      if (!pid) return;
      const prevVote = post.userVote ?? 0;
      const sendType = newVote === 0 ? prevVote : newVote;
      if (sendType !== 1 && sendType !== -1) return;

      const snapshot = posts;
      setPosts((prev) =>
        updatePostInTree(prev, pid, (p) => applyVoteOptimistic(p, newVote)),
      );
      try {
        const res = await forumService.vote('post', pid, sendType);
        setPosts((prev) =>
          updatePostInTree(prev, pid, (p) => ({
            ...p,
            upvotes: res.upvotes ?? p.upvotes,
            downvotes: res.downvotes ?? p.downvotes,
            score: res.score ?? p.score,
            userVote: newVote,
          })),
        );
      } catch (err) {
        setPosts(snapshot);
        showFeedback('error', extractForumError(err, 'Vote fehlgeschlagen'));
      }
    },
    [isAuthenticated, posts, showFeedback],
  );

  // ---------- Antworten ----------
  const submitReply = useCallback(
    async (content: string, parentPostId?: string | null) => {
      if (!topicId) return;
      try {
        await forumService.createPost({
          topicId,
          content,
          parentPostId: parentPostId || undefined,
        });
        setActiveReplyId(null);
        showFeedback('success', 'Antwort veröffentlicht');
        setTopic((t) =>
          t ? { ...t, postCount: (t.postCount ?? 0) + 1 } : t,
        );
        await fetchPosts(topicId);
      } catch (err) {
        throw new Error(
          extractForumError(err, 'Antwort konnte nicht gesendet werden'),
        );
      }
    },
    [topicId, fetchPosts, showFeedback],
  );

  const handleReplyClick = useCallback(
    (post: ForumPost) => {
      if (!isAuthenticated) {
        setLoginPrompt('Bitte melde dich an, um zu antworten.');
        return;
      }
      const pid = (post.id || post._id) as string | undefined;
      if (!pid) return;
      setActiveReplyId((prev) => (prev === pid ? null : pid));
    },
    [isAuthenticated],
  );

  // ---------- Abo ----------
  const handleToggleSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setLoginPrompt('Bitte melde dich an, um dieses Topic zu abonnieren.');
      return;
    }
    if (!topic || !topicId || subscribing) return;
    const wasSubscribed = topic.isSubscribed === true;
    setSubscribing(true);
    setTopic((t) => (t ? { ...t, isSubscribed: !wasSubscribed } : t));
    try {
      if (wasSubscribed) {
        await forumService.unsubscribe('topic', topicId);
        showFeedback('success', 'Benachrichtigungen deaktiviert');
      } else {
        await forumService.subscribe('topic', topicId);
        showFeedback('success', 'Du erhältst jetzt Benachrichtigungen');
      }
    } catch (err) {
      setTopic((t) => (t ? { ...t, isSubscribed: wasSubscribed } : t));
      showFeedback('error', extractForumError(err, 'Aktion fehlgeschlagen'));
    } finally {
      setSubscribing(false);
    }
  }, [isAuthenticated, topic, topicId, subscribing, showFeedback]);

  // ---------- Melden ----------
  const handleReportClick = useCallback(
    (targetType: 'topic' | 'post', targetId: string) => {
      if (!isAuthenticated) {
        setLoginPrompt('Bitte melde dich an, um Inhalte zu melden.');
        return;
      }
      setReportTarget({ targetType, targetId });
    },
    [isAuthenticated],
  );

  // ---------- Löschen ----------
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'topic') {
        await forumService.deleteTopic(deleteTarget.id);
        navigate('/forum');
        return;
      }
      await forumService.deletePost(deleteTarget.id);
      showFeedback('success', 'Beitrag gelöscht');
      setDeleteTarget(null);
      if (topicId) await fetchPosts(topicId);
    } catch (err) {
      showFeedback('error', extractForumError(err, 'Löschen fehlgeschlagen'));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, navigate, topicId, fetchPosts, showFeedback]);

  // ---------- Bearbeiten ----------
  const handlePostUpdated = useCallback((updated: ForumPost) => {
    const pid = (updated.id || updated._id) as string | undefined;
    if (!pid) return;
    setPosts((prev) =>
      updatePostInTree(prev, pid, (p) => ({
        ...p,
        content: updated.content ?? p.content,
        contentPlain: updated.contentPlain ?? p.contentPlain,
        isEdited: true,
        editedAt: updated.editedAt ?? Date.now(),
      })),
    );
    setEditingPost(null);
  }, []);

  const handleTopicUpdated = useCallback((updated: ForumTopic) => {
    setTopic((prev) =>
      prev
        ? {
            ...prev,
            title: updated.title ?? prev.title,
            content: updated.content ?? prev.content,
            contentPlain: updated.contentPlain ?? prev.contentPlain,
            tags: updated.tags ?? prev.tags,
            isEdited: true,
            editedAt: updated.editedAt ?? Date.now(),
          }
        : prev,
    );
    setEditTopicOpen(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Forum | DartCloud</title>
        </Helmet>
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" style={{ color: TEAL }} />
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Forum | DartCloud</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <AlertTriangle size={64} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 mb-4">{error || 'Topic nicht gefunden'}</p>
          <Button onPress={() => navigate('/forum')} variant="flat" className="bg-zinc-800">
            Zurück zum Forum
          </Button>
        </div>
      </div>
    );
  }

  const headerImg = forumService.buildFileUrl(topic.headerImageUrl || null);
  const created = forumService.formatRelativeTime(topic.createdAt);
  const edited = topic.isEdited
    ? forumService.formatRelativeTime(topic.editedAt)
    : null;
  const isSubscribed = topic.isSubscribed === true;

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>{topic.title || 'Topic'} | DartCloud Forum</title>
        {topic.contentPlain && (
          <meta name="description" content={topic.contentPlain.slice(0, 160)} />
        )}
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <Button
          variant="light"
          startContent={<ArrowLeft size={16} />}
          onPress={() => navigate('/forum')}
          className="mb-4 text-gray-400"
        >
          Zurück zum Forum
        </Button>

        {/* Feedback-Banner */}
        {feedback && (
          <div
            className={`flex items-center gap-2 rounded-xl px-4 py-3 mb-4 text-sm border ${
              feedback.type === 'success'
                ? 'border-zinc-700 text-white'
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}
            style={
              feedback.type === 'success'
                ? { backgroundColor: `${TEAL}1a`, borderColor: `${TEAL}55` }
                : undefined
            }
          >
            {feedback.type === 'success' ? (
              <CheckCircle2 size={16} style={{ color: TEAL }} />
            ) : (
              <AlertTriangle size={16} />
            )}
            <span className="flex-1">{feedback.text}</span>
            <button
              type="button"
              onClick={() => setFeedback(null)}
              className="text-gray-400 hover:text-white"
              aria-label="Schließen"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Header image */}
        {headerImg && (
          <Image
            src={headerImg}
            alt={topic.title || ''}
            className="w-full h-48 md:h-64 object-cover rounded-xl mb-4"
            removeWrapper
          />
        )}

        {/* Topic */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardBody className="p-5 md:p-6">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {topic.isPinned && (
                <Chip
                  size="sm"
                  startContent={<Pin size={12} />}
                  style={{ backgroundColor: `${TEAL}33`, color: TEAL }}
                >
                  Angeheftet
                </Chip>
              )}
              {topic.isLocked && (
                <Chip size="sm" startContent={<Lock size={12} />} color="warning" variant="flat">
                  Geschlossen
                </Chip>
              )}
              {topic.categoryName && (
                <Chip size="sm" variant="flat" className="bg-zinc-800 text-gray-400">
                  {topic.categoryName}
                </Chip>
              )}

              {/* Topic-Aktionen rechts */}
              <div className="ml-auto flex items-center gap-1">
                <Button
                  size="sm"
                  variant={isSubscribed ? 'flat' : 'light'}
                  isDisabled={subscribing}
                  onPress={handleToggleSubscription}
                  startContent={
                    isSubscribed ? (
                      <BellRing size={15} style={{ color: TEAL }} />
                    ) : (
                      <Bell size={15} />
                    )
                  }
                  className={
                    isSubscribed ? 'text-white' : 'text-gray-400'
                  }
                  style={
                    isSubscribed ? { backgroundColor: `${TEAL}26` } : undefined
                  }
                >
                  {isSubscribed ? 'Abonniert' : 'Abonnieren'}
                </Button>
                {!isTopicAuthor && (
                  <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    aria-label="Topic melden"
                    title="Topic melden"
                    className="text-gray-400"
                    onPress={() => topicId && handleReportClick('topic', topicId)}
                  >
                    <Flag size={15} />
                  </Button>
                )}
                {isTopicAuthor && (
                  <>
                    <Button
                      size="sm"
                      variant="light"
                      isIconOnly
                      aria-label="Topic bearbeiten"
                      title="Topic bearbeiten"
                      className="text-gray-400"
                      onPress={() => setEditTopicOpen(true)}
                    >
                      <Edit3 size={15} />
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      isIconOnly
                      aria-label="Topic löschen"
                      title="Topic löschen"
                      className="text-red-500"
                      onPress={() =>
                        topicId && setDeleteTarget({ type: 'topic', id: topicId })
                      }
                    >
                      <Trash2 size={15} />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold mb-3">
              {topic.title || 'Topic'}
            </h1>

            {/* Author meta */}
            <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Avatar
                  src={topic.authorAvatarUrl || undefined}
                  name={topic.authorName || '?'}
                  className="w-7 h-7 text-xs"
                />
                <span className="text-gray-300 font-medium">
                  {topic.authorName || 'Anonym'}
                </span>
              </div>
              <span>· {created}</span>
              {edited && (
                <span className="flex items-center gap-1 text-xs">
                  <Edit3 size={12} /> bearbeitet {edited}
                </span>
              )}
            </div>

            {/* Content */}
            <div className="prose prose-invert max-w-none text-gray-200">
              {topic.content ? (
                <div
                  className="whitespace-pre-wrap leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeBasic(
                      forumService.extractPlainText(topic.content),
                    ),
                  }}
                />
              ) : (
                <p className="text-gray-500 italic">Kein Inhalt</p>
              )}
            </div>

            {/* Tags */}
            {topic.tags && topic.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-4">
                {topic.tags.map((t, i) => (
                  <Chip
                    key={i}
                    size="sm"
                    variant="bordered"
                    className="border-zinc-700 text-gray-400"
                  >
                    #{t}
                  </Chip>
                ))}
              </div>
            )}

            {/* Votes + Stats */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500 mt-5 pt-4 border-t border-zinc-800">
              <VoteButtons
                score={topic.score ?? 0}
                userVote={topic.userVote}
                onVote={handleTopicVote}
              />
              <span className="flex items-center gap-1">
                <MessageCircle size={12} /> {topic.postCount ?? 0} Antworten
              </span>
              <span className="flex items-center gap-1">
                <Eye size={12} /> {topic.viewCount ?? 0} Aufrufe
              </span>
              {(topic.uniqueParticipants ?? 0) > 1 && (
                <span className="flex items-center gap-1">
                  <Users size={12} /> {topic.uniqueParticipants}
                </span>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Replies header + sort */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <MessageSquare size={20} style={{ color: TEAL }} />
            {topic.postCount ?? 0} Antworten
          </h2>
          <Tabs
            selectedKey={sortBy}
            onSelectionChange={(k) => setSortBy(k as SortMode)}
            size="sm"
            variant="solid"
            classNames={{
              tabList: 'bg-zinc-900 border border-zinc-800',
            }}
          >
            <Tab key="best" title="Best" />
            <Tab key="newest" title="Neueste" />
            <Tab key="top" title="Top" />
          </Tabs>
        </div>

        {/* Posts */}
        {loadingPosts ? (
          <div className="flex justify-center items-center h-32">
            <Spinner size="md" style={{ color: TEAL }} />
          </div>
        ) : posts.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardBody className="p-8 text-center">
              <MessageSquare size={48} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">Noch keine Antworten</p>
            </CardBody>
          </Card>
        ) : (
          <ul className="space-y-3">
            {posts.map((post) => (
              <li key={post.id || post._id}>
                <PostNode
                  post={post}
                  depth={0}
                  currentUserId={currentUserId}
                  isLocked={isLocked}
                  activeReplyId={activeReplyId}
                  onVote={handlePostVote}
                  onReply={handleReplyClick}
                  onEdit={(p) => setEditingPost(p)}
                  onDelete={(p) => {
                    const pid = (p.id || p._id) as string | undefined;
                    if (pid) setDeleteTarget({ type: 'post', id: pid });
                  }}
                  onReport={(p) => {
                    const pid = (p.id || p._id) as string | undefined;
                    if (pid) handleReportClick('post', pid);
                  }}
                  onSubmitReply={submitReply}
                  onCancelReply={() => setActiveReplyId(null)}
                />
              </li>
            ))}
          </ul>
        )}

        {/* Antwort-Composer unten */}
        <div className="mt-6">
          {isLocked ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardBody className="p-4 flex flex-row items-center gap-2 text-gray-500 text-sm">
                <Lock size={16} />
                Dieses Topic ist geschlossen. Neue Antworten sind nicht möglich.
              </CardBody>
            </Card>
          ) : !isAuthenticated ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardBody className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-gray-400 text-sm flex-1">
                  Melde dich an, um auf dieses Topic zu antworten.
                </p>
                <Button
                  size="sm"
                  style={{ backgroundColor: TEAL, color: '#000' }}
                  className="font-semibold"
                  onPress={() => navigate('/login')}
                >
                  Zum Login
                </Button>
              </CardBody>
            </Card>
          ) : (
            <ReplyComposer
              placeholder="Schreibe eine Antwort auf dieses Topic..."
              onSubmit={(content) => submitReply(content, null)}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <LoginPromptModal
        isOpen={loginPrompt !== null}
        onClose={() => setLoginPrompt(null)}
        message={loginPrompt || undefined}
      />

      {reportTarget && (
        <ReportModal
          isOpen
          onClose={() => setReportTarget(null)}
          targetType={reportTarget.targetType}
          targetId={reportTarget.targetId}
          onReported={() =>
            showFeedback('success', 'Danke für deine Meldung. Wir prüfen den Inhalt.')
          }
        />
      )}

      {topic && (
        <EditTopicModal
          isOpen={editTopicOpen}
          onClose={() => setEditTopicOpen(false)}
          topic={topic}
          onUpdated={handleTopicUpdated}
        />
      )}

      {editingPost && (
        <EditPostModal
          isOpen
          onClose={() => setEditingPost(null)}
          post={editingPost}
          onUpdated={handlePostUpdated}
        />
      )}

      <ConfirmDeleteModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        isLoading={deleting}
        title={
          deleteTarget?.type === 'topic' ? 'Topic löschen?' : 'Kommentar löschen?'
        }
        message={
          deleteTarget?.type === 'topic'
            ? 'Möchtest du dieses Topic wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
            : 'Möchtest du diesen Kommentar wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
        }
      />
    </div>
  );
};

// ============= Post Node (recursive) =============
interface PostNodeProps {
  post: ForumPost;
  depth: number;
  currentUserId: string | null;
  isLocked: boolean;
  activeReplyId: string | null;
  onVote: (post: ForumPost, newVote: 1 | -1 | 0) => void;
  onReply: (post: ForumPost) => void;
  onEdit: (post: ForumPost) => void;
  onDelete: (post: ForumPost) => void;
  onReport: (post: ForumPost) => void;
  onSubmitReply: (content: string, parentPostId?: string | null) => Promise<void>;
  onCancelReply: () => void;
}

const MAX_INDENT_DEPTH = 5;

const PostNode: React.FC<PostNodeProps> = ({
  post,
  depth,
  currentUserId,
  isLocked,
  activeReplyId,
  onVote,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onSubmitReply,
  onCancelReply,
}) => {
  const created = forumService.formatRelativeTime(post.createdAt);
  const edited = post.isEdited ? forumService.formatRelativeTime(post.editedAt) : null;
  const indent = Math.min(depth, MAX_INDENT_DEPTH);
  const isDeleted = post.isDeleted || post.showAsDeleted;
  const postId = (post.id || post._id) as string | undefined;
  const isOwn = !!currentUserId && !!post.authorId && post.authorId === currentUserId;
  const isReplying = !!postId && activeReplyId === postId;

  return (
    <div style={{ marginLeft: `${indent * 16}px` }}>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardBody className="p-4">
          <div className="flex items-center gap-2 mb-2">
            {depth > 0 && (
              <CornerDownRight size={14} className="text-gray-600 flex-shrink-0" />
            )}
            <Avatar
              src={post.authorAvatarUrl || undefined}
              name={post.authorName || '?'}
              className="w-6 h-6 text-[10px]"
            />
            <span className="text-sm font-medium text-gray-200">
              {isDeleted ? '[gelöscht]' : post.authorName || 'Anonym'}
            </span>
            <span className="text-xs text-gray-500">· {created}</span>
            {edited && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Edit3 size={10} /> bearbeitet {edited}
              </span>
            )}
            {isDeleted && (
              <Chip
                size="sm"
                variant="flat"
                className="bg-zinc-800 text-gray-500 text-[10px] h-5"
                startContent={<Trash2 size={10} />}
              >
                Gelöscht
              </Chip>
            )}
          </div>

          {/* Content */}
          <div className="ml-8">
            {isDeleted ? (
              <p className="text-gray-500 italic text-sm">
                Dieser Beitrag wurde entfernt.
              </p>
            ) : (
              <div
                className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: sanitizeBasic(
                    forumService.extractPlainText(post.content || ''),
                  ),
                }}
              />
            )}

            {/* Footer */}
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
              {!isDeleted && (
                <VoteButtons
                  size="sm"
                  score={post.score ?? 0}
                  userVote={post.userVote}
                  onVote={(v) => onVote(post, v)}
                />
              )}
              {isDeleted && (
                <span className="font-bold text-gray-500">{post.score ?? 0}</span>
              )}

              {!isDeleted && !isLocked && (
                <button
                  type="button"
                  onClick={() => onReply(post)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-zinc-800 hover:text-white transition-colors"
                >
                  <Reply size={12} />
                  Antworten
                </button>
              )}

              {!isDeleted && isOwn && (
                <>
                  <button
                    type="button"
                    onClick={() => onEdit(post)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-zinc-800 hover:text-white transition-colors"
                  >
                    <Edit3 size={12} />
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(post)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-zinc-800 text-red-500/80 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={12} />
                    Löschen
                  </button>
                </>
              )}

              {!isDeleted && !isOwn && (
                <button
                  type="button"
                  onClick={() => onReport(post)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-zinc-800 hover:text-white transition-colors"
                  title="Beitrag melden"
                >
                  <Flag size={12} />
                  Melden
                </button>
              )}

              {(post.replyCount ?? 0) > 0 && (
                <span className="flex items-center gap-1 ml-1">
                  <MessageCircle size={12} />
                  {post.replyCount} {post.replyCount === 1 ? 'Antwort' : 'Antworten'}
                </span>
              )}
            </div>

            {/* Inline-Antwort-Composer */}
            {isReplying && (
              <div className="mt-3">
                <ReplyComposer
                  replyToName={post.authorName || 'Anonym'}
                  autoFocus
                  onSubmit={(content) => onSubmitReply(content, postId)}
                  onCancel={onCancelReply}
                />
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Replies */}
      {post.replies && post.replies.length > 0 && (
        <ul className="mt-2 space-y-2">
          {post.replies.map((reply) => (
            <li key={reply.id || reply._id}>
              <PostNode
                post={reply}
                depth={depth + 1}
                currentUserId={currentUserId}
                isLocked={isLocked}
                activeReplyId={activeReplyId}
                onVote={onVote}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onReport={onReport}
                onSubmitReply={onSubmitReply}
                onCancelReply={onCancelReply}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Minimal HTML sanitizer (strips script/style/on-attrs)
function sanitizeBasic(html: string): string {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}

export default ForumTopicPage;
